import { useCallback } from "react";

import { Action, Module, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";
const REPLAN_ACCESS_TYPES = [Action.RunTargetedReplan];

const hasReplanAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(REPLAN_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can replan a run.
 * A user needs either write access to the entity or the RunReplan permission to replan a run.
 * @returns {(entity?: Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">) => AccessResult} Function that takes an optional run and entity and returns a AccessResult indicating if user can replan the run
 */
const useCanReplanRun = (): ((
  entity?: Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">
) => AccessResult) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (entity) => {
      if (!entity) {
        return { result: false };
      }

      const hasAccessToReview =
        hasEntityWriteAccess(entity) ||
        hasReplanAccess(viewer, entity?.spaceDetails?.id || entity?.space);

      if (hasAccessToReview) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasEntityWriteAccess, viewer]
  );
};

export default useCanReplanRun;
